import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home as HomeIcon, User, Users, Calendar, MapPin, Ruler, Clock, CheckCircle2, AlertTriangle, Plus, X, Loader2, Wrench, ExternalLink, Info, ClipboardList, FileText, Gavel, Tag, Landmark, Trash2, Briefcase, Sparkles, Settings, Search, TrendingUp, DollarSign } from '../../lib/icons';
import { createHomeInvitation, removeHomeMember, revokeHomeInvitation } from '../../lib/household';
import ConfigureHomeSystemsModal from '../../components/ConfigureHomeSystemsModal';
import { supabase } from '../../lib/supabase';
import { Home, Profile, ServiceRequestExpense } from './types';
import { formatDateOnly, formatDateForDisplay } from '../../lib/dateUtils';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/constants';
import {
  DEFAULT_VISIT_DURATION_BY_TYPE,
  VisitDurationDefaults,
  VISIT_DURATION_OPTIONS_MINUTES,
  formatVisitDurationLabel,
} from '../../lib/visitDuration';
import { loadVisitDurationDefaults } from '../../lib/visitDurationDefaults';
import AdminLayout from './AdminLayout';
import {
  LEAD_SOURCES,
  type LeadSource,
  isLeadSource,
  BOOKING_SOURCES,
  BOOKING_SOURCE_LABELS,
} from '../../lib/attribution';
import { buildExpenseContextFields, getExpenseContextLabel } from '../../lib/adminExpenses';

interface Task {
  id: string;
  title: string;
  description: string;
  category_id: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  due_date: string | null;
  completed_at: string | null;
  home_id: string;
  category_name: string;
  skill_level: 'diy' | 'handyman' | 'professional';
  task_type: 'user_task' | 'maintenance_task';
  frequency_months: number | null;
  maintenance_task_id: string | null;
  completed_by?: 'homeowner' | 'professional' | null;
  completion_notes?: string | null;
}

interface TaskCategory {
  id: string;
  name: string;
  description: string;
}

interface Visit {
  id: string;
  visit_type: 'maintenance' | 'service' | 'walkthrough';
  status: 'requested' | 'proposed' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  scheduled_date: string;
  estimated_duration_minutes: number;
  actual_start_time: string | null;
  actual_end_time: string | null;
  assigned_to: string | null;
  contractor_company: string | null;
  internal_name: string | null;
  visit_notes: string | null;
  cost: number | null;
  total_tasks: number;
  completed_tasks: number;
}

interface PropertyRecord {
  id: string;
  record_type: 'permit' | 'tax_assessment' | 'sale' | 'lien' | 'other';
  title: string;
  description?: string;
  date: string;
  amount?: number;
  url?: string;
  metadata?: any;
}

interface HouseholdMember {
  id: string;
  user_id: string;
  role: 'owner' | 'co_owner' | 'member';
  created_at: string;
  profile_name: string;
}

interface HouseholdInvitation {
  id: string;
  email: string;
  role: 'co_owner' | 'member';
  status: string;
  expires_at: string;
}

interface BillingPlanPriceOption {
  product_id: string;
  product_slug: string;
  product_name: string;
  plan_value: string | null;
  stripe_price_id: string;
  stripe_product_id: string | null;
  label: string;
  amount_cents: number;
  currency: string;
  interval: string | null;
  admin_only: boolean;
}

const getBillingPlanPriceKey = (option: BillingPlanPriceOption) =>
  `${option.product_id}:${option.stripe_price_id}`;

type HomeExpense = ServiceRequestExpense & {
  visit?: { id: string; scheduled_date: string | null; visit_type: string | null; internal_name: string | null } | null;
  service_request?: { id: string; title: string | null } | null;
  children?: { id: string; amount: number; internal_cost: number | null }[] | null;
};

const formatHouseholdRole = (role: string) => role.replace('_', ' ');

const getFunctionErrorMessage = async (error: any, fallback: string) => {
  if (!error) return fallback;
  if (error.name === 'FunctionsHttpError' && error.context) {
    try {
      const payload = await error.context.json();
      if (typeof payload?.error === 'string' && payload.error.trim()) {
        return payload.error;
      }
      if (typeof payload?.message === 'string' && payload.message.trim()) {
        return payload.message;
      }
    } catch {
      // Fall back to the generic function error below.
    }
  }
  return error.message || fallback;
};

const ensureAuthenticatedAdminSession = async () => {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('Not authenticated. Sign in again and retry.');
  }
};

const HomeDetail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const homeId = searchParams.get('id');
  const userId = searchParams.get('userId');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [home, setHome] = useState<Home | null>(null);
  const [owner, setOwner] = useState<Profile | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [categories, setCategories] = useState<TaskCategory[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [records, setRecords] = useState<PropertyRecord[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'visits' | 'tasks' | 'records' | 'systems' | 'maintenance' | 'expenses'>('info');
  const [showConfigureSystemsModal, setShowConfigureSystemsModal] = useState(false);
  const [homeSystems, setHomeSystems] = useState<any>(null);
  const [homeSystemsLoading, setHomeSystemsLoading] = useState(false);
  const [maintenancePlans, setMaintenancePlans] = useState<any[]>([]);
  const [maintenancePlansLoading, setMaintenancePlansLoading] = useState(false);
  const [savingHomeManager, setSavingHomeManager] = useState(false);
  const [savingHomePlan, setSavingHomePlan] = useState(false);
  const [showAssignStripePlanPanel, setShowAssignStripePlanPanel] = useState(false);
  const [loadingHomePlanPrices, setLoadingHomePlanPrices] = useState(false);
  const [homePlanPrices, setHomePlanPrices] = useState<BillingPlanPriceOption[]>([]);
  const [selectedHomePlanPriceKey, setSelectedHomePlanPriceKey] = useState('');
  const [homePlanSuccess, setHomePlanSuccess] = useState<string | null>(null);
  const [homePlanPanelError, setHomePlanPanelError] = useState<string | null>(null);
  const [homeManagerError, setHomeManagerError] = useState<string | null>(null);
  // Attribution / source editor state
  const [teamMembers, setTeamMembers] = useState<Array<{ id: string; full_name: string | null }>>([]);
  const [referrerProfile, setReferrerProfile] = useState<{ id: string; full_name: string | null } | null>(null);
  const [referrerSearch, setReferrerSearch] = useState('');
  const [referrerSearchResults, setReferrerSearchResults] = useState<Array<{ id: string; full_name: string | null; phone_number: string | null }>>([]);
  const [referrerSearching, setReferrerSearching] = useState(false);
  const [showReferrerDropdown, setShowReferrerDropdown] = useState(false);
  const [savingAttribution, setSavingAttribution] = useState(false);
  const [attributionError, setAttributionError] = useState<string | null>(null);
  const [attributionSuccess, setAttributionSuccess] = useState<string | null>(null);
  const [attributionDraft, setAttributionDraft] = useState<{
    lead_source: LeadSource | '';
    signed_up_by: string;
    referred_by_profile_id: string;
    referral_source_notes: string;
  }>({
    lead_source: '',
    signed_up_by: '',
    referred_by_profile_id: '',
    referral_source_notes: '',
  });
  const [showCustomTaskModal, setShowCustomTaskModal] = useState(false);
  const [inventoryItems, setInventoryItems] = useState<Array<{id: string; name: string; category: string}>>([]);
  const [customTaskData, setCustomTaskData] = useState({
    task_name: '',
    description: '',
    frequency_months: '12',
    inventory_item_id: '',
    first_due_date: formatDateOnly(new Date())
  });
  const [creatingCustomTask, setCreatingCustomTask] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showScheduleVisitModal, setShowScheduleVisitModal] = useState(false);
  const [schedulingVisit, setSchedulingVisit] = useState(false);
  const [visitFormData, setVisitFormData] = useState({
    visit_type: 'service' as 'maintenance' | 'service' | 'walkthrough',
    scheduled_date: '',
    estimated_duration_minutes: DEFAULT_VISIT_DURATION_BY_TYPE.service,
    assigned_to: 'internal',
    internal_name: '',
    visit_notes: '',
    booked_by: '' as string,
    booking_source: 'admin_manual' as 'admin_manual' | 'homeowner_request' | 'automation',
  });
  const [scheduleVisitAdmins, setScheduleVisitAdmins] = useState<
    Array<{ id: string; full_name: string | null }>
  >([]);
  const [scheduleVisitCurrentUserId, setScheduleVisitCurrentUserId] = useState<string | null>(null);
  const [visitDurationDefaults, setVisitDurationDefaults] = useState<VisitDurationDefaults>({
    ...DEFAULT_VISIT_DURATION_BY_TYPE,
  });
  
  const [showAddRecordModal, setShowAddRecordModal] = useState(false);
  const [addingRecord, setAddingRecord] = useState(false);
  const [recordFormData, setRecordFormData] = useState({
    record_type: 'permit' as PropertyRecord['record_type'],
    title: '',
    description: '',
    date: formatDateOnly(new Date()),
    amount: '',
    url: '',
  });
  
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [completionData, setCompletionData] = useState({
    completion_date: formatDateOnly(new Date()),
    performed_by: 'homeowner' as 'homeowner' | 'professional',
    notes: ''
  });
  const [completing, setCompleting] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [markets, setMarkets] = useState<Array<{id: string; name: string; walkthrough_type: 'in_person' | 'virtual'}>>([]);
  const [updatingMarket, setUpdatingMarket] = useState(false);
  const [coordsDraft, setCoordsDraft] = useState<{ lat: string; lng: string } | null>(null);
  const [savingCoords, setSavingCoords] = useState(false);
  const [householdLoading, setHouseholdLoading] = useState(false);
  const [householdError, setHouseholdError] = useState<string | null>(null);
  const [householdMembers, setHouseholdMembers] = useState<HouseholdMember[]>([]);
  const [householdInvitations, setHouseholdInvitations] = useState<HouseholdInvitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'co_owner' | 'member'>('co_owner');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [homeExpenses, setHomeExpenses] = useState<HomeExpense[]>([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [showInternalCosts, setShowInternalCosts] = useState(false);
  const [filterBillableOnly, setFilterBillableOnly] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [savingExpense, setSavingExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState<HomeExpense | null>(null);
  const [expenseFormData, setExpenseFormData] = useState({
    amount: '',
    description: '',
    expense_date: formatDateOnly(new Date()),
    category: '',
    vendor: '',
    visit_id: '',
    billable_to_homeowner: true,
    needs_contractor_payment: true,
  });

  useEffect(() => {
    if (!homeId) {
      navigate('/admin');
      return;
    }
    fetchHomeData();
    fetchCategories();
    fetchVisits();
    fetchHomeExpenses();
    fetchRecords();
    fetchMarkets();
    fetchVisitDurationDefaults();
    void fetchTeamMembersForAttribution();
  }, [homeId]);

  useEffect(() => {
    void (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setScheduleVisitCurrentUserId(user?.id ?? null);
        setVisitFormData((prev) => ({
          ...prev,
          booked_by: prev.booked_by || user?.id || '',
        }));
        const { data, error } = await supabase
          .from('profiles')
          .select('id, full_name')
          .eq('is_system_manager', true)
          .order('full_name');
        if (error) throw error;
        setScheduleVisitAdmins(data || []);
      } catch (err) {
        console.error('Failed to load schedule-visit attribution context:', err);
      }
    })();
  }, []);

  // Sync the attribution editor draft + the looked-up referrer whenever the
  // underlying home record changes (load / save). Keeping derived state out
  // of `home` avoids stale-form bugs after a successful save.
  useEffect(() => {
    if (!home) return;
    setAttributionDraft({
      lead_source: isLeadSource(home.lead_source) ? home.lead_source : '',
      signed_up_by: home.signed_up_by || '',
      referred_by_profile_id: home.referred_by_profile_id || '',
      referral_source_notes: home.referral_source_notes || '',
    });
    if (home.referred_by_profile_id) {
      void fetchReferrerProfile(home.referred_by_profile_id);
    } else {
      setReferrerProfile(null);
      setReferrerSearch('');
    }
  }, [home?.id, home?.lead_source, home?.signed_up_by, home?.referred_by_profile_id, home?.referral_source_notes]);

  const fetchTeamMembersForAttribution = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('is_system_manager', true)
        .order('full_name', { ascending: true });
      if (error) throw error;
      setTeamMembers(data || []);
    } catch (err) {
      console.error('Failed to load team members for attribution:', err);
    }
  };

  const fetchReferrerProfile = async (profileId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('id', profileId)
        .maybeSingle();
      if (error) throw error;
      setReferrerProfile(data ? { id: data.id, full_name: data.full_name } : null);
      if (data?.full_name) setReferrerSearch(data.full_name);
    } catch (err) {
      console.error('Failed to load referrer profile:', err);
    }
  };

  const handleReferrerSearch = async (term: string) => {
    setReferrerSearch(term);
    setShowReferrerDropdown(true);
    const trimmed = term.trim();
    if (trimmed.length < 2) {
      setReferrerSearchResults([]);
      return;
    }
    try {
      setReferrerSearching(true);
      const likeTerm = `%${trimmed.replace(/,/g, ' ')}%`;
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, phone_number')
        .or(`full_name.ilike.${likeTerm},phone_number.ilike.${likeTerm}`)
        .eq('is_system_manager', false)
        .limit(10);
      if (error) throw error;
      setReferrerSearchResults(data || []);
    } catch (err) {
      console.error('Failed to search referrer profiles:', err);
      setReferrerSearchResults([]);
    } finally {
      setReferrerSearching(false);
    }
  };

  const handleSaveAttribution = async () => {
    if (!homeId) return;
    setSavingAttribution(true);
    setAttributionError(null);
    setAttributionSuccess(null);
    try {
      const payload: Record<string, any> = {
        lead_source: attributionDraft.lead_source || null,
        signed_up_by: attributionDraft.signed_up_by || null,
        // referred_by + notes are only meaningful when source = Referral.
        // Clear them otherwise so the data stays consistent.
        referred_by_profile_id:
          attributionDraft.lead_source === 'Referral'
            ? attributionDraft.referred_by_profile_id || null
            : null,
        referral_source_notes:
          attributionDraft.lead_source === 'Referral'
            ? attributionDraft.referral_source_notes.trim() || null
            : null,
      };
      const { error: updateError } = await supabase
        .from('homes')
        .update(payload)
        .eq('id', homeId);
      if (updateError) throw updateError;

      setAttributionSuccess('Attribution saved');
      setTimeout(() => setAttributionSuccess(null), 2500);
      await fetchHomeData();
    } catch (err: any) {
      console.error('Failed to save attribution:', err);
      setAttributionError(err?.message || 'Failed to save attribution');
    } finally {
      setSavingAttribution(false);
    }
  };

  const attributionIsDirty = (() => {
    if (!home) return false;
    const currentSource = isLeadSource(home.lead_source) ? home.lead_source : '';
    if (attributionDraft.lead_source !== currentSource) return true;
    if ((attributionDraft.signed_up_by || '') !== (home.signed_up_by || '')) return true;
    if (attributionDraft.lead_source === 'Referral') {
      if ((attributionDraft.referred_by_profile_id || '') !== (home.referred_by_profile_id || '')) return true;
      if (attributionDraft.referral_source_notes.trim() !== (home.referral_source_notes || '').trim()) return true;
    }
    return false;
  })();

  const fetchMarkets = async () => {
    try {
      const { data, error } = await supabase
        .from('markets')
        .select('id, name, walkthrough_type')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setMarkets(data || []);
    } catch (error) {
      console.error('Error fetching markets:', error);
    }
  };

  const fetchVisitDurationDefaults = async () => {
    try {
      const defaults = await loadVisitDurationDefaults();
      setVisitDurationDefaults(defaults);
    } catch (error: any) {
      console.error('Error fetching visit duration defaults:', error);
    }
  };

  const handleUpdateMarket = async (marketId: string | null) => {
    if (!homeId) return;

    setUpdatingMarket(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('homes')
        .update({ market_id: marketId })
        .eq('id', homeId);

      if (updateError) throw updateError;

      // Refresh home data to get updated market info
      await fetchHomeData();
    } catch (error: any) {
      console.error('Error updating market:', error);
      setError(error.message || 'Failed to update market');
    } finally {
      setUpdatingMarket(false);
    }
  };

  const handleUpdateHomeManager = async (newHomeManager: string) => {
    if (!homeId) return;

    setSavingHomeManager(true);
    setHomeManagerError(null);

    try {
      const { error: updateError } = await supabase
        .from('homes')
        .update({ home_manager: newHomeManager })
        .eq('id', homeId);

      if (updateError) throw updateError;

      // The DB trigger updates home_manager_assigned_at and syncs the
      // per-home manager conversation. Refresh to pull the new metadata.
      await fetchHomeData();
    } catch (err: any) {
      console.error('Error updating home manager:', err);
      setHomeManagerError(err.message || 'Failed to update home manager');
    } finally {
      setSavingHomeManager(false);
    }
  };

  useEffect(() => {
    if (homeId) {
      fetchTasks();
      if (activeTab === 'maintenance') {
        fetchMaintenancePlans();
      }
    }
  }, [homeId, statusFilter, priorityFilter, typeFilter]);

  useEffect(() => {
    if (homeId && activeTab === 'maintenance') {
      fetchMaintenancePlans();
    }
    if (homeId && activeTab === 'systems') {
      void fetchHomeSystems();
    }
  }, [homeId, activeTab]);

  const fetchHomeSystems = async () => {
    if (!homeId) return;
    setHomeSystemsLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('home_systems')
        .select('*')
        .eq('home_id', homeId)
        .maybeSingle();
      if (fetchError) throw fetchError;
      setHomeSystems(data);
    } catch (err) {
      console.error('Error fetching home systems:', err);
    } finally {
      setHomeSystemsLoading(false);
    }
  };

  const fetchHomeData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch home data
      const { data: homeData, error: homeError } = await supabase
        .from('homes')
        .select('*')
        .eq('id', homeId)
        .single();

      if (homeError) throw homeError;

      setHome(homeData);
      setHomePlanSuccess(null);

      // Fetch owner data only when the home has an owner. Some homes (e.g.
      // imported property records) are unowned, and querying with a null id
      // would throw "invalid input syntax for type uuid: null".
      if (homeData.owner_id) {
        const { data: ownerData, error: ownerError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', homeData.owner_id)
          .single();

        if (ownerError) throw ownerError;
        setOwner(ownerData);
      } else {
        setOwner(null);
      }

      await fetchHouseholdData(homeData.id);
    } catch (error: any) {
      console.error('Error fetching home data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminHomePlanPrices = async () => {
    if (!home?.id) return;

    try {
      setLoadingHomePlanPrices(true);
      setHomePlanPanelError(null);
      await ensureAuthenticatedAdminSession();

      const { data, error: listError } = await supabase.functions.invoke('admin-list-home-billing-prices', {
        body: { homeId: home.id },
      });

      if (listError) {
        throw new Error(await getFunctionErrorMessage(listError, 'Failed to load billing prices'));
      }
      if (data?.error) {
        throw new Error(data.error);
      }

      const prices = (data?.prices || []) as BillingPlanPriceOption[];
      setHomePlanPrices(prices);
      setSelectedHomePlanPriceKey((prev) => {
        if (prev && prices.some((price) => getBillingPlanPriceKey(price) === prev)) return prev;
        return prices[0] ? getBillingPlanPriceKey(prices[0]) : '';
      });
    } catch (planPriceError: any) {
      console.error('Error loading home billing prices:', planPriceError);
      setHomePlanPrices([]);
      setSelectedHomePlanPriceKey('');
      setHomePlanPanelError(planPriceError.message || 'Failed to load billing prices');
    } finally {
      setLoadingHomePlanPrices(false);
    }
  };

  const handleOpenAssignStripePlanPanel = async () => {
    setShowAssignStripePlanPanel(true);
    setHomePlanPanelError(null);
    if (homePlanPrices.length === 0) {
      await fetchAdminHomePlanPrices();
    }
  };

  const handleCloseAssignStripePlanPanel = () => {
    setShowAssignStripePlanPanel(false);
    setHomePlanPanelError(null);
  };

  const handleAssignStripeHomePlan = async () => {
    if (!home?.id || !owner?.id || !selectedHomePlanPriceKey) return;

    const selectedPrice = homePlanPrices.find(
      (price) => getBillingPlanPriceKey(price) === selectedHomePlanPriceKey,
    );
    if (!selectedPrice) return;

    const confirmed = window.confirm(
      `Assign "${selectedPrice.label}" to this home?\n\nThis creates a real Stripe subscription for ${owner.full_name || 'the owner'}.`,
    );
    if (!confirmed) return;

    try {
      setSavingHomePlan(true);
      setHomePlanSuccess(null);
      setHomePlanPanelError(null);
      setError(null);
      await ensureAuthenticatedAdminSession();

      const { data, error: assignError } = await supabase.functions.invoke('admin-assign-home-subscription', {
        body: {
          homeId: home.id,
          productId: selectedPrice.product_id,
          stripePriceId: selectedPrice.stripe_price_id,
          payerProfileId: owner.id,
        },
      });

      if (assignError) {
        throw new Error(await getFunctionErrorMessage(assignError, 'Failed to assign Stripe plan'));
      }
      if (data?.error) {
        throw new Error(data.error);
      }

      setHomePlanSuccess(`Assigned ${data?.product?.name || selectedPrice.product_name} to this home.`);
      setShowAssignStripePlanPanel(false);
      await fetchHomeData();
    } catch (assignError: any) {
      console.error('Error assigning Stripe home plan:', assignError);
      setHomePlanPanelError(assignError.message || 'Failed to assign Stripe plan');
    } finally {
      setSavingHomePlan(false);
    }
  };

  const fetchHouseholdData = async (targetHomeId?: string) => {
    const resolvedHomeId = targetHomeId || homeId;
    if (!resolvedHomeId) return;

    try {
      setHouseholdLoading(true);
      setHouseholdError(null);

      const { data: memberRows, error: memberRowsError } = await supabase
        .from('home_members')
        .select('id, user_id, role, created_at')
        .eq('home_id', resolvedHomeId)
        .is('removed_at', null)
        .order('created_at', { ascending: true });

      if (memberRowsError) throw memberRowsError;

      const profileIds = (memberRows || []).map((member) => member.user_id);
      const profileNameById = new Map<string, string>();

      if (profileIds.length > 0) {
        const { data: profileRows, error: profileRowsError } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', profileIds);

        if (profileRowsError) throw profileRowsError;

        (profileRows || []).forEach((profileRow) => {
          profileNameById.set(profileRow.id, profileRow.full_name || 'Household user');
        });
      }

      setHouseholdMembers(
        (memberRows || []).map((memberRow) => ({
          ...memberRow,
          profile_name: profileNameById.get(memberRow.user_id) || 'Household user',
        })) as HouseholdMember[]
      );

      const { data: invitationRows, error: invitationRowsError } = await supabase
        .from('home_invitations')
        .select('id, email, role, status, expires_at')
        .eq('home_id', resolvedHomeId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (invitationRowsError) throw invitationRowsError;
      setHouseholdInvitations((invitationRows || []) as HouseholdInvitation[]);
    } catch (householdFetchError: any) {
      console.error('Error fetching household data:', householdFetchError);
      setHouseholdError(householdFetchError.message || 'Failed to load household data');
      setHouseholdMembers([]);
      setHouseholdInvitations([]);
    } finally {
      setHouseholdLoading(false);
    }
  };

  const handleSendHouseholdInvite = async () => {
    if (!homeId || !inviteEmail.trim()) return;

    try {
      setInviteSubmitting(true);
      setHouseholdError(null);
      await createHomeInvitation({
        homeId,
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setInviteEmail('');
      await fetchHouseholdData(homeId);
    } catch (inviteError: any) {
      setHouseholdError(inviteError.message || 'Failed to send invite');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleRevokeHouseholdInvite = async (invitationId: string) => {
    try {
      setHouseholdError(null);
      await revokeHomeInvitation(invitationId);
      await fetchHouseholdData(homeId || undefined);
    } catch (revokeError: any) {
      setHouseholdError(revokeError.message || 'Failed to revoke invite');
    }
  };

  const handleRemoveHouseholdMember = async (memberUserId: string) => {
    if (!homeId) return;

    const member = householdMembers.find((row) => row.user_id === memberUserId);
    if (!member || member.role === 'owner') return;

    if (!window.confirm(`Remove ${member.profile_name} from this household?`)) {
      return;
    }

    try {
      setHouseholdError(null);
      await removeHomeMember(homeId, memberUserId);
      await fetchHouseholdData(homeId);
    } catch (removeError: any) {
      setHouseholdError(removeError.message || 'Failed to remove member');
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('task_categories')
        .select('*')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchVisits = async () => {
    try {
      setVisitsLoading(true);
      
      const { data, error } = await supabase
        .from('visits_with_details')
        .select('*')
        .eq('home_id', homeId)
        .order('scheduled_date', { ascending: false });

      if (error) throw error;
      setVisits(data || []);
    } catch (error) {
      console.error('Error fetching visits:', error);
    } finally {
      setVisitsLoading(false);
    }
  };

  const fetchHomeExpenses = async () => {
    if (!homeId) return;
    try {
      setExpensesLoading(true);
      const { data, error } = await supabase
        .from('service_request_expenses')
        .select(`
          *,
          service_request:service_requests(id, title),
          visit:visits(id, scheduled_date, visit_type, internal_name),
          children:service_request_expenses!parent_expense_id(id, amount, internal_cost)
        `)
        .eq('home_id', homeId)
        .is('parent_expense_id', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setHomeExpenses((data || []) as HomeExpense[]);
    } catch (fetchError) {
      console.error('Error fetching home expenses:', fetchError);
    } finally {
      setExpensesLoading(false);
    }
  };

  const resetExpenseForm = () => {
    setEditingExpense(null);
    setExpenseFormData({
      amount: '',
      description: '',
      expense_date: formatDateOnly(new Date()),
      category: '',
      vendor: '',
      visit_id: '',
      billable_to_homeowner: true,
      needs_contractor_payment: true,
    });
  };

  const handleOpenExpenseModal = (expense?: HomeExpense) => {
    if (expense) {
      setEditingExpense(expense);
      setExpenseFormData({
        amount: String(expense.amount ?? ''),
        description: expense.description || '',
        expense_date: expense.expense_date || '',
        category: expense.category || '',
        vendor: expense.vendor || '',
        visit_id: expense.visit_id || '',
        billable_to_homeowner: expense.billable_to_homeowner,
        needs_contractor_payment: expense.needs_contractor_payment,
      });
    } else {
      resetExpenseForm();
    }
    setShowExpenseModal(true);
  };

  const handleSaveHomeExpense = async () => {
    if (!homeId) return;
    const parsedAmount = parseFloat(expenseFormData.amount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Expense amount must be greater than zero');
      return;
    }

    try {
      setSavingExpense(true);
      setError(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const contextFields = buildExpenseContextFields({ type: 'home', homeId });
      const payload = {
        ...contextFields,
        visit_id: expenseFormData.visit_id || null,
        amount: parsedAmount,
        description: expenseFormData.description || null,
        expense_date: expenseFormData.expense_date || null,
        category: expenseFormData.category || null,
        vendor: expenseFormData.vendor || null,
        parent_expense_id: null,
        quote_id: null,
        billable_to_homeowner: expenseFormData.billable_to_homeowner,
        needs_contractor_payment: expenseFormData.needs_contractor_payment,
        homeowner_visible: true,
        internal_cost: null,
        created_by: user.id,
      };

      if (editingExpense) {
        const { error: updateError } = await supabase
          .from('service_request_expenses')
          .update(payload)
          .eq('id', editingExpense.id);
        if (updateError) throw updateError;
      } else {
        const { error: createError } = await supabase
          .from('service_request_expenses')
          .insert(payload);
        if (createError) throw createError;
      }

      setShowExpenseModal(false);
      resetExpenseForm();
      await fetchHomeExpenses();
    } catch (saveError: any) {
      console.error('Error saving home expense:', saveError);
      setError(saveError.message || 'Failed to save expense');
    } finally {
      setSavingExpense(false);
    }
  };

  const handleDeleteHomeExpense = async (expenseId: string) => {
    try {
      const { error } = await supabase
        .from('service_request_expenses')
        .delete()
        .eq('id', expenseId);
      if (error) throw error;
      await fetchHomeExpenses();
    } catch (deleteError: any) {
      console.error('Error deleting home expense:', deleteError);
      setError(deleteError.message || 'Failed to delete expense');
    }
  };

  const fetchRecords = async () => {
    try {
      setRecordsLoading(true);
      const { data, error } = await supabase
        .from('property_records')
        .select('*')
        .eq('home_id', homeId)
        .order('date', { ascending: false });

      if (error) throw error;
      setRecords(data || []);
    } catch (error) {
      console.error('Error fetching records:', error);
    } finally {
      setRecordsLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      setTasksLoading(true);
      
      let query = supabase
        .from('combined_tasks')
        .select('*')
        .eq('home_id', homeId);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (priorityFilter !== 'all') {
        query = query.eq('priority', priorityFilter);
      }

      if (typeFilter !== 'all') {
        query = query.eq('task_type', typeFilter);
      }

      const { data, error } = await query.order('due_date', { ascending: true });

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setTasksLoading(false);
    }
  };

  const fetchMaintenancePlans = async () => {
    if (!homeId) return;
    
    setMaintenancePlansLoading(true);
    try {
      const { data, error } = await supabase
        .from('maintenance_plans')
        .select(`
          *,
          maintenance_tasks (
            id,
            name,
            description,
            frequency_months,
            estimated_duration_minutes
          )
        `)
        .eq('home_id', homeId)
        .order('next_due_date', { ascending: true });

      if (error) throw error;
      
      // Get system info from maintenance_templates by matching task IDs
      if (data && data.length > 0) {
        const taskIds = data.map(plan => plan.task_id).filter(Boolean);
        
        if (taskIds.length > 0) {
          const { data: templates, error: templatesError } = await supabase
            .from('maintenance_templates')
            .select('id, system_type, system_name, priority, professional_required')
            .in('id', taskIds);
          
          if (!templatesError && templates) {
            // Map templates to plans
            const templatesMap = templates.reduce((acc, template) => {
              acc[template.id] = template;
              return acc;
            }, {} as Record<string, any>);
            
            const plansWithTemplates = data.map(plan => ({
              ...plan,
              maintenance_templates: templatesMap[plan.task_id] || null
            }));
            
            setMaintenancePlans(plansWithTemplates);
            return;
          }
        }
      }
      
      setMaintenancePlans(data || []);
    } catch (error) {
      console.error('Error fetching maintenance plans:', error);
      setMaintenancePlans([]);
    } finally {
      setMaintenancePlansLoading(false);
    }
  };

  const fetchInventoryItems = async () => {
    if (!homeId) return;
    
    try {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('id, name, category')
        .eq('home_id', homeId)
        .order('name');

      if (error) throw error;
      setInventoryItems(data || []);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      setInventoryItems([]);
    }
  };

  const handleOpenCustomTaskModal = () => {
    setCustomTaskData({
      task_name: '',
      description: '',
      frequency_months: '12',
      inventory_item_id: '',
      first_due_date: formatDateOnly(new Date())
    });
    fetchInventoryItems();
    setShowCustomTaskModal(true);
  };

  const handleCloseCustomTaskModal = () => {
    setShowCustomTaskModal(false);
    setCustomTaskData({
      task_name: '',
      description: '',
      frequency_months: '12',
      inventory_item_id: '',
      first_due_date: formatDateOnly(new Date())
    });
  };

  const handleCreateCustomTask = async () => {
    if (!homeId || !customTaskData.task_name.trim()) {
      setError('Please provide a task name');
      return;
    }

    const frequencyMonths = parseInt(customTaskData.frequency_months);
    if (isNaN(frequencyMonths) || frequencyMonths < 1) {
      setError('Please provide a valid frequency (at least 1 month)');
      return;
    }

    setCreatingCustomTask(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc('create_custom_maintenance_task', {
        p_home_id: homeId,
        p_task_name: customTaskData.task_name.trim(),
        p_description: customTaskData.description.trim() || null,
        p_frequency_months: frequencyMonths,
        p_inventory_item_id: customTaskData.inventory_item_id || null,
        p_first_due_date: customTaskData.first_due_date || null
      });

      if (error) throw error;

      // Refresh maintenance plans
      await fetchMaintenancePlans();
      handleCloseCustomTaskModal();
    } catch (error: any) {
      console.error('Error creating custom task:', error);
      setError(error.message || 'Failed to create custom task. Please try again.');
    } finally {
      setCreatingCustomTask(false);
    }
  };

  const updateTaskStatus = async (taskId: string, newStatus: Task['status']) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ 
          status: newStatus,
          completed_at: newStatus === 'completed' ? new Date().toISOString() : null
        })
        .eq('id', taskId);

      if (error) throw error;

      // Update local state
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { 
                ...task, 
                status: newStatus,
                completed_at: newStatus === 'completed' ? new Date().toISOString() : null
              }
            : task
        )
      );
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const handleOpenCompleteModal = (task: Task) => {
    setSelectedTask(task);
    setCompletionData({
      completion_date: formatDateOnly(new Date()),
      performed_by: 'homeowner',
      notes: ''
    });
    setShowCompleteModal(true);
  };

  const handleCloseCompleteModal = () => {
    setShowCompleteModal(false);
    setSelectedTask(null);
    setCompletionData({
      completion_date: formatDateOnly(new Date()),
      performed_by: 'homeowner',
      notes: ''
    });
  };

  const handleCompleteTask = async () => {
    if (!selectedTask) return;
    
    setCompleting(true);
    setError(null);

    try {
      if (selectedTask.task_type === 'maintenance_task') {
        // Use the new completion function for maintenance tasks
        const { error } = await supabase.rpc('complete_maintenance_task', {
          plan_id: selectedTask.id,
          completion_date: completionData.completion_date,
          performed_by: completionData.performed_by,
          notes: completionData.notes || null,
          provider_id: null
        });

        if (error) throw error;
      } else {
        // For user tasks, use the standard update
        const { error } = await supabase
          .from('honeydone_tasks')
          .update({ 
            status: 'completed',
            completed_at: completionData.completion_date
          })
          .eq('id', selectedTask.id);

        if (error) throw error;
      }
      
      // Refresh the tasks
      fetchTasks();
      handleCloseCompleteModal();
    } catch (error: any) {
      console.error('Error completing task:', error);
      setError('Failed to mark task as complete. Please try again.');
    } finally {
      setCompleting(false);
    }
  };

  const handleSaveCoords = async () => {
    if (!homeId || !coordsDraft) return;
    const lat = parseFloat(coordsDraft.lat);
    const lng = parseFloat(coordsDraft.lng);
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setError('Invalid coordinates. Latitude must be −90 to 90, longitude −180 to 180.');
      return;
    }
    setSavingCoords(true);
    setError(null);
    try {
      const { error: updateError } = await supabase
        .from('homes')
        .update({ latitude: lat, longitude: lng })
        .eq('id', homeId);
      if (updateError) throw updateError;
      setHome((prev) => prev ? { ...prev, latitude: lat, longitude: lng } : prev);
      setCoordsDraft(null);
    } catch (err: any) {
      setError(err.message || 'Failed to save coordinates.');
    } finally {
      setSavingCoords(false);
    }
  };

  const handleEnrichHomeData = async () => {
    if (!home || !homeId) return;

    setEnriching(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/enrich-home-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          address: home.address,
          city: home.city,
          state: home.state,
          zip_code: home.zip_code,
          home_id: homeId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to enrich home data' }));
        throw new Error(errorData.error || 'Failed to enrich home data');
      }

      const result = await response.json();

      if (result.success) {
        // Refresh home data to get updated enrichment fields and last_enriched_at
        await fetchHomeData();
      } else {
        throw new Error(result.error || 'Enrichment completed but returned no data');
      }
    } catch (error: any) {
      console.error('Error enriching home data:', error);
      setError(error.message || 'Failed to enrich home data. Please try again.');
    } finally {
      setEnriching(false);
    }
  };

  const handleAddRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!homeId) return;

    setAddingRecord(true);
    try {
      const { error } = await supabase
        .from('property_records')
        .insert([{
          home_id: homeId,
          record_type: recordFormData.record_type,
          title: recordFormData.title,
          description: recordFormData.description || null,
          date: recordFormData.date,
          amount: recordFormData.amount ? parseFloat(recordFormData.amount) : null,
          url: recordFormData.url || null,
        }]);

      if (error) throw error;

      setRecordFormData({
        record_type: 'permit',
        title: '',
        description: '',
        date: formatDateOnly(new Date()),
        amount: '',
        url: '',
      });
      setShowAddRecordModal(false);
      fetchRecords();
    } catch (error: any) {
      console.error('Error adding record:', error);
      setError(error.message);
    } finally {
      setAddingRecord(false);
    }
  };

  const handleDeleteRecord = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      const { error } = await supabase
        .from('property_records')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setRecords(records.filter(r => r.id !== id));
    } catch (error) {
      console.error('Error deleting record:', error);
    }
  };

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSkillLevelColor = (skillLevel: Task['skill_level']) => {
    switch (skillLevel) {
      case 'diy': return 'bg-green-100 text-green-800';
      case 'handyman': return 'bg-yellow-100 text-yellow-800';
      case 'professional': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVisitStatusColor = (status: Visit['status']) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'scheduled': return 'bg-yellow-100 text-yellow-800';
      case 'proposed': return 'bg-orange-100 text-orange-800';
      case 'requested': return 'bg-purple-100 text-purple-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'no_show': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVisitTypeColor = (type: Visit['visit_type']) => {
    switch (type) {
      case 'maintenance': return 'bg-purple-100 text-purple-800';
      case 'service': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRecordIcon = (type: PropertyRecord['record_type']) => {
    switch (type) {
      case 'permit': return FileText;
      case 'tax_assessment': return Landmark;
      case 'sale': return Tag;
      case 'lien': return Gavel;
      default: return Info;
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleScheduleVisit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!homeId || !home) return;

    setSchedulingVisit(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create the visit
      const visitInsert: Record<string, unknown> = {
        home_id: homeId,
        visit_type: visitFormData.visit_type,
        scheduled_date: new Date(visitFormData.scheduled_date).toISOString(),
        estimated_duration_minutes: visitFormData.estimated_duration_minutes,
        assigned_to: visitFormData.assigned_to,
        internal_name: visitFormData.internal_name.trim() ? visitFormData.internal_name.trim() : null,
        visit_notes: visitFormData.visit_notes || null,
        created_by: user.id,
        updated_by: user.id,
      };
      if (visitFormData.visit_type === 'walkthrough') {
        visitInsert.booked_by = visitFormData.booked_by || user.id;
        visitInsert.booking_source = visitFormData.booking_source || 'admin_manual';
      }

      const { data: visitData, error: visitError } = await supabase
        .from('visits')
        .insert([visitInsert])
        .select()
        .single();

      if (visitError) throw visitError;

      // Reset form and close modal
      setVisitFormData({
        visit_type: 'service',
        scheduled_date: '',
        estimated_duration_minutes: visitDurationDefaults.service,
        assigned_to: 'internal',
        internal_name: '',
        visit_notes: '',
        booked_by: scheduleVisitCurrentUserId || '',
        booking_source: 'admin_manual',
      });
      setShowScheduleVisitModal(false);

      // Refresh the visits list
      await fetchVisits();
    } catch (error: any) {
      console.error('Error scheduling visit:', error);
      setError(error.message);
    } finally {
      setSchedulingVisit(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout activeTab="homes">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !home) {
    return (
      <AdminLayout activeTab="homes">
        <div className="p-6">
          <div className="bg-red-50 text-red-700 p-4 rounded-lg">
            {error || 'Home not found'}
          </div>
        </div>
      </AdminLayout>
    );
  }

  const completedTasks = tasks.filter(task => task.status === 'completed').length;
  const totalTasks = tasks.length;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <AdminLayout activeTab="homes">
    <div style={{ overflowY: 'auto', height: '100%' }}>
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6 space-y-3">
        <button
          onClick={() => userId ? navigate(`/admin/profile?id=${userId}`) : navigate('/admin')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5 mr-1" />
          {userId ? 'Back to Profile' : 'Back to Admin'}
        </button>
        <div>
          <h1 className="text-2xl font-bold">{home.address}</h1>
          <p className="text-gray-600">{home.city}, {home.state} {home.zip_code}</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <nav className="border-b border-gray-200 mb-6">
        <div className="flex space-x-4 overflow-x-auto">
          <button
            onClick={() => setActiveTab('info')}
            className={`px-4 py-2 border-b-2 whitespace-nowrap ${
              activeTab === 'info'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Info className="w-4 h-4" />
              <span>Basic Information</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('visits')}
            className={`px-4 py-2 border-b-2 whitespace-nowrap ${
              activeTab === 'visits'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4" />
              <span>Visits</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`px-4 py-2 border-b-2 whitespace-nowrap ${
              activeTab === 'tasks'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <ClipboardList className="w-4 h-4" />
              <span>HoneyDone List</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('records')}
            className={`px-4 py-2 border-b-2 whitespace-nowrap ${
              activeTab === 'records'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <FileText className="w-4 h-4" />
              <span>Records</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('expenses')}
            className={`px-4 py-2 border-b-2 whitespace-nowrap ${
              activeTab === 'expenses'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <DollarSign className="w-4 h-4" />
              <span>Expenses</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('systems')}
            className={`px-4 py-2 border-b-2 whitespace-nowrap ${
              activeTab === 'systems'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Settings className="w-4 h-4" />
              <span>Home Systems</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('maintenance')}
            className={`px-4 py-2 border-b-2 whitespace-nowrap ${
              activeTab === 'maintenance'
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Wrench className="w-4 h-4" />
              <span>Maintenance Plan</span>
            </div>
          </button>
        </div>
      </nav>
      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Home Information */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold flex items-center mb-4">
              <HomeIcon className="w-5 h-5 mr-2 text-gray-500" />
              Property Information
            </h2>
            {home.cover_photo_url && (
              <div className="mb-6">
                <img
                  src={home.cover_photo_url}
                  alt="Property cover photo"
                  className="w-full h-48 object-cover rounded-lg border"
                />
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-500">Address</label>
                <div className="font-medium flex items-center">
                  <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                  {home.address}
                </div>
                <div className="text-sm text-gray-500">
                  {home.city}, {home.state} {home.zip_code}
                </div>
                {/* Map coordinates — subtle inline editor for admins */}
                {coordsDraft ? (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <input
                      type="text"
                      placeholder="Lat"
                      value={coordsDraft.lat}
                      onChange={(e) => setCoordsDraft({ ...coordsDraft, lat: e.target.value })}
                      className="w-24 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-black"
                    />
                    <input
                      type="text"
                      placeholder="Lng"
                      value={coordsDraft.lng}
                      onChange={(e) => setCoordsDraft({ ...coordsDraft, lng: e.target.value })}
                      className="w-24 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-black"
                    />
                    <button
                      onClick={handleSaveCoords}
                      disabled={savingCoords}
                      className="text-xs px-2 py-0.5 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50"
                    >
                      {savingCoords ? '…' : 'Save'}
                    </button>
                    <button
                      onClick={() => setCoordsDraft(null)}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setCoordsDraft({
                      lat: home.latitude != null ? String(home.latitude) : '',
                      lng: home.longitude != null ? String(home.longitude) : '',
                    })}
                    className="mt-1 text-xs text-gray-400 hover:text-gray-600 flex items-center gap-0.5"
                  >
                    <MapPin className="w-3 h-3" />
                    {home.latitude != null && home.longitude != null
                      ? `${home.latitude.toFixed(4)}, ${home.longitude.toFixed(4)}`
                      : 'Set map coordinates'}
                  </button>
                )}
              </div>
              {home.square_footage && (
                <div>
                  <label className="text-sm text-gray-500">Square Footage</label>
                  <div className="font-medium flex items-center">
                    <Ruler className="w-4 h-4 mr-1 text-gray-400" />
                    {home.square_footage.toLocaleString()} sq ft
                  </div>
                </div>
              )}
              {home.year_built && (
                <div>
                  <label className="text-sm text-gray-500">Year Built</label>
                  <div className="font-medium flex items-center">
                    <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                    {home.year_built}
                  </div>
                </div>
              )}
              {home.lot_size && (
                <div>
                  <label className="text-sm text-gray-500">Lot Size</label>
                  <div className="font-medium flex items-center">
                    <Ruler className="w-4 h-4 mr-1 text-gray-400" />
                    {home.lot_size.toLocaleString()} sq ft
                  </div>
                </div>
              )}
              {home.bedrooms && (
                <div>
                  <label className="text-sm text-gray-500">Bedrooms</label>
                  <div className="font-medium flex items-center">
                    <HomeIcon className="w-4 h-4 mr-1 text-gray-400" />
                    {home.bedrooms} {home.bedrooms === 1 ? 'bedroom' : 'bedrooms'}
                  </div>
                </div>
              )}
              {home.bathrooms && (
                <div>
                  <label className="text-sm text-gray-500">Bathrooms</label>
                  <div className="font-medium flex items-center">
                    <HomeIcon className="w-4 h-4 mr-1 text-gray-400" />
                    {home.bathrooms} {home.bathrooms === 1 ? 'bathroom' : 'bathrooms'}
                  </div>
                </div>
              )}
              <div>
                <label className="text-sm text-gray-500">Added to System</label>
                <div className="font-medium flex items-center">
                  <Clock className="w-4 h-4 mr-1 text-gray-400" />
                  {formatDateForDisplay(home.created_at)}
                </div>
              </div>
              {home.home_notes && (
                <div>
                  <label className="text-sm text-gray-500">Home Notes</label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-md text-sm">
                    {home.home_notes}
                  </div>
                </div>
              )}
              <div>
                <label className="text-sm text-gray-500">Last Enriched</label>
                <div className="font-medium flex items-center">
                  <Clock className="w-4 h-4 mr-1 text-gray-400" />
                  {home.last_enriched_at 
                    ? formatDateForDisplay(home.last_enriched_at)
                    : 'Never enriched'}
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Market</label>
                <div className="flex items-center space-x-2">
                  <select
                    value={home.market_id || ''}
                    onChange={(e) => handleUpdateMarket(e.target.value || null)}
                    disabled={updatingMarket}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">No Market (Uses Default)</option>
                    {markets.map((market) => (
                      <option key={market.id} value={market.id}>
                        {market.name} {market.walkthrough_type === 'virtual' ? '(Virtual)' : '(In-Person)'}
                      </option>
                    ))}
                  </select>
                  {updatingMarket && (
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {home.market_id 
                    ? 'This home is assigned to a specific market'
                    : 'This home will use the default market for walkthrough availability'}
                </p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={handleEnrichHomeData}
                disabled={enriching}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enriching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Enriching...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span>Enrich Home Data</span>
                  </>
                )}
              </button>
              <p className="mt-2 text-xs text-gray-500 text-center">
                This will fetch property details from public sources and update the home information.
              </p>
            </div>
          </div>

          {/* Owner Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold flex items-center mb-4">
              <User className="w-5 h-5 mr-2 text-gray-500" />
              Owner Information
            </h2>
            {owner ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-500">Name</label>
                  <div className="font-medium">{owner.full_name}</div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Phone</label>
                  <div className="font-medium">{owner.phone_number || 'N/A'}</div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Plan</label>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-md text-sm ${
                      home?.plan === 'managed'
                        ? 'bg-black text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {home?.plan === 'managed' ? 'Premium' : 'Basic'}
                    </span>
                    {home?.plan_source ? (
                      <span className="text-xs text-gray-500 capitalize">
                        Source: {home.plan_source}
                      </span>
                    ) : null}
                  </div>
                  {home?.plan_subscription_id ? (
                    <div className="mt-2 text-xs text-gray-500 font-mono truncate" title={home.plan_subscription_id}>
                      {home.plan_subscription_id}
                    </div>
                  ) : null}
                  {homePlanSuccess ? (
                    <div className="mt-2 text-xs text-green-700">{homePlanSuccess}</div>
                  ) : null}
                  {!showAssignStripePlanPanel ? (
                    <button
                      type="button"
                      onClick={handleOpenAssignStripePlanPanel}
                      disabled={!owner?.id || Boolean(home?.plan_subscription_id)}
                      className={`mt-3 text-sm underline-offset-2 hover:underline ${
                        !owner?.id || home?.plan_subscription_id
                          ? 'text-gray-400 cursor-not-allowed no-underline'
                          : 'text-gray-600 hover:text-black'
                      }`}
                    >
                      {home?.plan_subscription_id
                        ? 'Home already has an active Stripe subscription'
                        : 'Assign Stripe plan...'}
                    </button>
                  ) : (
                    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium text-gray-900">Assign Stripe plan</div>
                          <p className="mt-1 text-xs text-gray-500">
                            Creates a real subscription in Stripe. Use only when setting up or correcting billing.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleCloseAssignStripePlanPanel}
                          disabled={savingHomePlan}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                          aria-label="Close assign plan panel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      {!owner?.stripe_customer_id ? (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                          Connect a Stripe customer on{' '}
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/profile?id=${owner.id}`)}
                            className="underline font-medium"
                          >
                            this owner&apos;s profile
                          </button>{' '}
                          before assigning a plan.
                        </div>
                      ) : null}

                      <div>
                        <label className="text-xs font-medium text-gray-600">Stripe price</label>
                        <select
                          value={selectedHomePlanPriceKey}
                          onChange={(e) => setSelectedHomePlanPriceKey(e.target.value)}
                          disabled={loadingHomePlanPrices || savingHomePlan || homePlanPrices.length === 0}
                          className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        >
                          {loadingHomePlanPrices ? (
                            <option value="">Loading prices...</option>
                          ) : homePlanPrices.length === 0 ? (
                            <option value="">No Stripe prices available</option>
                          ) : (
                            homePlanPrices.map((price) => (
                              <option key={getBillingPlanPriceKey(price)} value={getBillingPlanPriceKey(price)}>
                                {price.label}
                              </option>
                            ))
                          )}
                        </select>
                        {selectedHomePlanPriceKey ? (
                          <div className="mt-1 text-[11px] text-gray-500 font-mono break-all">
                            {homePlanPrices.find((price) => getBillingPlanPriceKey(price) === selectedHomePlanPriceKey)?.stripe_price_id}
                          </div>
                        ) : null}
                      </div>

                      {homePlanPanelError ? (
                        <div className="text-xs text-red-600">{homePlanPanelError}</div>
                      ) : null}

                      <div className="flex flex-wrap gap-2 pt-1">
                        <button
                          type="button"
                          onClick={handleAssignStripeHomePlan}
                          disabled={
                            savingHomePlan ||
                            loadingHomePlanPrices ||
                            !owner?.stripe_customer_id ||
                            !selectedHomePlanPriceKey
                          }
                          className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm ${
                            savingHomePlan ||
                            loadingHomePlanPrices ||
                            !owner?.stripe_customer_id ||
                            !selectedHomePlanPriceKey
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-black text-white hover:bg-gray-800'
                          }`}
                        >
                          {savingHomePlan ? 'Assigning...' : 'Confirm assignment'}
                        </button>
                        <button
                          type="button"
                          onClick={() => fetchAdminHomePlanPrices()}
                          disabled={savingHomePlan || loadingHomePlanPrices}
                          className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm border ${
                            savingHomePlan || loadingHomePlanPrices
                              ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          Refresh prices
                        </button>
                        <button
                          type="button"
                          onClick={handleCloseAssignStripePlanPanel}
                          disabled={savingHomePlan}
                          className="inline-flex items-center px-3 py-1.5 rounded-md text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic">
                This home has no owner assigned yet.
              </div>
            )}
          </div>

          {/* Household */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold flex items-center mb-4">
              <Users className="w-5 h-5 mr-2 text-gray-500" />
              Household
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Invite a co-owner or member to share access to this home. They will receive an email with an acceptance link.
            </p>

            {householdError && (
              <div className="mb-4 text-sm text-red-600">{householdError}</div>
            )}

            {householdLoading ? (
              <div className="flex items-center text-sm text-gray-500">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading household...
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <label className="text-sm text-gray-500">Invite household user</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setInviteRole('co_owner')}
                      className={`rounded-full px-3 py-1 text-sm border ${
                        inviteRole === 'co_owner'
                          ? 'border-black bg-black text-white'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Co-owner
                    </button>
                    <button
                      type="button"
                      onClick={() => setInviteRole('member')}
                      className={`rounded-full px-3 py-1 text-sm border ${
                        inviteRole === 'member'
                          ? 'border-black bg-black text-white'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Member
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleSendHouseholdInvite}
                    disabled={inviteSubmitting || !inviteEmail.trim()}
                    className="mt-3 w-full flex items-center justify-center px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {inviteSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Invite'
                    )}
                  </button>
                </div>

                <div>
                  <label className="text-sm text-gray-500">Members</label>
                  {householdMembers.length === 0 ? (
                    <p className="mt-1 text-sm text-gray-500 italic">No household members yet.</p>
                  ) : (
                    <ul className="mt-2 divide-y divide-gray-100 border border-gray-200 rounded-md">
                      {householdMembers.map((member) => (
                        <li key={member.id} className="flex items-center justify-between px-3 py-2 text-sm">
                          <div>
                            <button
                              type="button"
                              onClick={() => navigate(`/admin/profile?id=${member.user_id}`)}
                              className="font-medium text-black hover:underline"
                            >
                              {member.profile_name}
                            </button>
                            <span className="ml-2 text-gray-500 capitalize">
                              {formatHouseholdRole(member.role)}
                            </span>
                          </div>
                          {member.role !== 'owner' ? (
                            <button
                              type="button"
                              onClick={() => handleRemoveHouseholdMember(member.user_id)}
                              className="text-red-600 hover:text-red-800 text-xs"
                            >
                              Remove
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <label className="text-sm text-gray-500">Pending invites</label>
                  {householdInvitations.length === 0 ? (
                    <p className="mt-1 text-sm text-gray-500 italic">No pending invites.</p>
                  ) : (
                    <ul className="mt-2 divide-y divide-gray-100 border border-gray-200 rounded-md">
                      {householdInvitations.map((invitation) => (
                        <li key={invitation.id} className="flex items-center justify-between px-3 py-2 text-sm">
                          <div>
                            <div className="font-medium">{invitation.email}</div>
                            <div className="text-xs text-gray-500 capitalize">
                              {formatHouseholdRole(invitation.role)} · Expires{' '}
                              {formatDateForDisplay(invitation.expires_at)}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRevokeHouseholdInvite(invitation.id)}
                            className="text-red-600 hover:text-red-800 text-xs shrink-0 ml-3"
                          >
                            Revoke
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Home Manager */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold flex items-center mb-4">
              <Briefcase className="w-5 h-5 mr-2 text-gray-500" />
              Home Manager
            </h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-500">Assigned manager</label>
                <select
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black disabled:opacity-60"
                  value={home?.home_manager || 'Unassigned'}
                  disabled={savingHomeManager}
                  onChange={(e) => handleUpdateHomeManager(e.target.value)}
                >
                  <option value="Unassigned">Unassigned</option>
                  {teamMembers
                    .filter((m) => (m.full_name || '').trim().length > 0)
                    .map((member) => (
                      <option key={member.id} value={member.full_name as string}>
                        {member.full_name}
                      </option>
                    ))}
                  {home?.home_manager &&
                    home.home_manager !== 'Unassigned' &&
                    !teamMembers.some((m) => m.full_name === home.home_manager) && (
                      <option value={home.home_manager}>
                        {home.home_manager} (not on team list)
                      </option>
                    )}
                </select>
              </div>
              {home?.home_manager_assigned_at && (
                <div className="text-xs text-gray-500">
                  Assigned {formatDateForDisplay(home.home_manager_assigned_at)}
                </div>
              )}
              {savingHomeManager && (
                <div className="flex items-center text-xs text-gray-500">
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Saving...
                </div>
              )}
              {homeManagerError && (
                <div className="text-xs text-red-600">{homeManagerError}</div>
              )}
              <p className="text-xs text-gray-500">
                Changing the manager updates this home only and re-routes the homeowner's per-home conversation.
              </p>
            </div>
          </div>

          {/* Attribution / Source */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold flex items-center mb-4">
              <TrendingUp className="w-5 h-5 mr-2 text-gray-500" />
              Attribution
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-500">Signed up by</label>
                <select
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                  value={attributionDraft.signed_up_by}
                  onChange={(e) =>
                    setAttributionDraft((prev) => ({ ...prev, signed_up_by: e.target.value }))
                  }
                  disabled={savingAttribution}
                >
                  <option value="">— Unattributed —</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.full_name || 'Unnamed team member'}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Team member who signed this home up. Used for performance reporting (separate from current home manager).
                </p>
              </div>

              <div>
                <label className="text-sm text-gray-500">Lead source</label>
                <select
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                  value={attributionDraft.lead_source}
                  onChange={(e) =>
                    setAttributionDraft((prev) => ({
                      ...prev,
                      lead_source: e.target.value as LeadSource | '',
                      // Clear referrer when leaving Referral so the next save normalizes the row
                      referred_by_profile_id:
                        e.target.value === 'Referral' ? prev.referred_by_profile_id : '',
                      referral_source_notes:
                        e.target.value === 'Referral' ? prev.referral_source_notes : '',
                    }))
                  }
                  disabled={savingAttribution}
                >
                  <option value="">— Unknown —</option>
                  {LEAD_SOURCES.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
              </div>

              {attributionDraft.lead_source === 'Referral' && (
                <>
                  <div className="relative">
                    <label className="text-sm text-gray-500">Referred by (homeowner)</label>
                    <div className="mt-1 relative">
                      <input
                        type="text"
                        value={referrerSearch}
                        onChange={(e) => handleReferrerSearch(e.target.value)}
                        onFocus={() => setShowReferrerDropdown(true)}
                        onBlur={() => setTimeout(() => setShowReferrerDropdown(false), 150)}
                        placeholder="Search by name or phone…"
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-9 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                        disabled={savingAttribution}
                      />
                      <Search className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      {attributionDraft.referred_by_profile_id && (
                        <button
                          type="button"
                          onClick={() => {
                            setAttributionDraft((prev) => ({ ...prev, referred_by_profile_id: '' }));
                            setReferrerProfile(null);
                            setReferrerSearch('');
                          }}
                          className="absolute right-7 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {showReferrerDropdown && referrerSearch.trim().length >= 2 && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {referrerSearching ? (
                          <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>
                        ) : referrerSearchResults.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">No matches.</div>
                        ) : (
                          referrerSearchResults.map((profile) => (
                            <button
                              key={profile.id}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setAttributionDraft((prev) => ({
                                  ...prev,
                                  referred_by_profile_id: profile.id,
                                }));
                                setReferrerProfile({ id: profile.id, full_name: profile.full_name });
                                setReferrerSearch(profile.full_name || '');
                                setShowReferrerDropdown(false);
                              }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                            >
                              <span className="font-medium text-gray-900 truncate">
                                {profile.full_name || 'Unnamed'}
                              </span>
                              {profile.phone_number && (
                                <span className="ml-3 text-xs text-gray-500 shrink-0">{profile.phone_number}</span>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                    {referrerProfile && !showReferrerDropdown && (
                      <p className="mt-1 text-xs text-gray-500">
                        Linked to {referrerProfile.full_name || 'unnamed homeowner'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm text-gray-500">Referral notes (optional)</label>
                    <textarea
                      rows={2}
                      value={attributionDraft.referral_source_notes}
                      onChange={(e) =>
                        setAttributionDraft((prev) => ({
                          ...prev,
                          referral_source_notes: e.target.value,
                        }))
                      }
                      placeholder="e.g. Neighbor of John Smith; partner program ABC"
                      className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                      disabled={savingAttribution}
                    />
                  </div>
                </>
              )}

              {attributionError && (
                <div className="text-xs text-red-600">{attributionError}</div>
              )}
              {attributionSuccess && (
                <div className="text-xs text-green-600">{attributionSuccess}</div>
              )}

              <div className="flex items-center justify-end gap-2 pt-1">
                {attributionIsDirty && !savingAttribution && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!home) return;
                      setAttributionDraft({
                        lead_source: isLeadSource(home.lead_source) ? home.lead_source : '',
                        signed_up_by: home.signed_up_by || '',
                        referred_by_profile_id: home.referred_by_profile_id || '',
                        referral_source_notes: home.referral_source_notes || '',
                      });
                      setAttributionError(null);
                    }}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    Reset
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSaveAttribution}
                  disabled={savingAttribution || !attributionIsDirty}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {savingAttribution && <Loader2 className="w-3 h-3 animate-spin" />}
                  Save attribution
                </button>
              </div>
            </div>
          </div>

          {/* Task Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Task Summary</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Tasks</span>
                <span className="font-semibold">{totalTasks}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Completed</span>
                <span className="font-semibold text-green-600">{completedTasks}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Completion Rate</span>
                <span className="font-semibold">{completionRate}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${completionRate}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {activeTab === 'visits' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-gray-500" />
                Recent Visits
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowScheduleVisitModal(true)}
                  className="flex items-center space-x-1 px-3 py-1 text-sm bg-black text-white hover:bg-gray-800 rounded-md transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Schedule Visit</span>
                </button>
                <button
                  onClick={() => navigate(`/admin/visits?homeId=${homeId}`)}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                >
                  View All
                  <ExternalLink className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>

            {visitsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : visits.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Visits Yet</h3>
                <p className="text-gray-600 mb-4">No visits have been scheduled for this home.</p>
                <button
                  onClick={() => setShowScheduleVisitModal(true)}
                  className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                >
                  Schedule First Visit
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {visits.slice(0, 5).map((visit) => (
                  <div key={visit.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${getVisitTypeColor(visit.visit_type)}`}>
                            {visit.visit_type}
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${getVisitStatusColor(visit.status)}`}>
                            {visit.status.replace('_', ' ')}
                          </span>
                        </div>

                        {visit.internal_name && (
                          <div className="text-sm font-medium text-gray-900 mb-1 truncate">
                            {visit.internal_name}
                          </div>
                        )}

                        <div className="text-sm text-gray-600 mb-2">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{formatDateTime(visit.scheduled_date)}</span>
                          </div>
                        </div>

                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          {visit.assigned_to && (
                            <div className="flex items-center space-x-1">
                              <Wrench className="w-3 h-3" />
                              <span>{visit.assigned_to === 'internal' ? 'Internal' : visit.assigned_to}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center space-x-1">
                            <CheckCircle2 className="w-3 h-3" />
                            <span>{visit.completed_tasks}/{visit.total_tasks} tasks</span>
                          </div>
                        </div>

                        {visit.visit_notes && (
                          <p className="text-xs text-gray-600 mt-2 italic truncate">
                            "{visit.visit_notes}"
                          </p>
                        )}
                      </div>

                      <button
                        onClick={() => navigate(`/admin/visit?id=${visit.id}`)}
                        className="text-xs text-blue-600 hover:text-blue-800 ml-2"
                      >
                        View
                      </button>
                    </div>
                  </div>
                ))}
                
                {visits.length > 5 && (
                  <div className="text-center pt-4">
                    <button
                      onClick={() => navigate(`/admin/visits?homeId=${homeId}`)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      View {visits.length - 5} more visits
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'tasks' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold">HoneyDone List</h2>
              <button
                onClick={fetchTasks}
                disabled={tasksLoading}
                className="flex items-center space-x-2 px-3 py-1 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                {tasksLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <span>Refresh</span>
                )}
              </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>

              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm"
              >
                <option value="all">All Priority</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>

              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm"
              >
                <option value="all">All Types</option>
                <option value="user_task">User Tasks</option>
                <option value="maintenance_task">Maintenance Tasks</option>
              </select>
            </div>

            {/* Tasks List */}
            <div className="space-y-4">
              {tasksLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : tasks.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Tasks Found</h3>
                  <p className="text-gray-600">No tasks match the current filters.</p>
                </div>
              ) : (
                tasks.map((task) => (
                  <div key={task.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{task.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        {task.status !== 'completed' && task.status !== 'cancelled' && (
                          task.task_type === 'maintenance_task' ? (
                            <button
                              onClick={() => handleOpenCompleteModal(task)}
                              className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm flex items-center space-x-1"
                              title="Mark as completed"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              <span>Complete</span>
                            </button>
                          ) : (
                          <button
                            onClick={() => updateTaskStatus(task.id, 'completed')}
                            className="text-green-600 hover:text-green-800"
                            title="Mark as completed"
                          >
                            <CheckCircle2 className="w-5 h-5" />
                          </button>
                          )
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${getStatusColor(task.status)}`}>
                        {task.status.replace('_', ' ')}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${getSkillLevelColor(task.skill_level)}`}>
                        {task.skill_level}
                      </span>
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-purple-100 text-purple-800">
                        {task.task_type === 'user_task' ? 'User Task' : 'Maintenance'}
                      </span>
                      {task.category_name && (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-800">
                          {task.category_name}
                        </span>
                      )}
                      {task.frequency_months && (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800">
                          Every {task.frequency_months} month{task.frequency_months !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                    {task.due_date && (
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>Due: {formatDateForDisplay(task.due_date)}</span>
                      </div>
                    )}
                    {task.completed_at && (
                        <div className="flex items-center space-x-1 text-green-600">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Completed: {formatDateForDisplay(task.completed_at)}</span>
                        </div>
                      )}
                      {task.completed_by && (
                        <div className="flex items-center space-x-1">
                          {task.completed_by === 'homeowner' ? (
                            <User className="w-4 h-4" />
                          ) : (
                            <Briefcase className="w-4 h-4" />
                          )}
                          <span>{task.completed_by === 'homeowner' ? 'Homeowner' : 'Professional'}</span>
                        </div>
                      )}
                    </div>

                    {task.completion_notes && (
                      <div className="mt-2 text-sm text-gray-600 italic bg-gray-50 p-2 rounded">
                        Note: "{task.completion_notes}"
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'records' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold flex items-center">
                <FileText className="w-5 h-5 mr-2 text-gray-500" />
                Property Records
              </h2>
              <button
                onClick={() => setShowAddRecordModal(true)}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-black text-white hover:bg-gray-800 rounded-md transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add Record</span>
              </button>
            </div>

            {recordsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : records.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Records Yet</h3>
                <p className="text-gray-600 mb-4">No property records have been added.</p>
                <button
                  onClick={() => setShowAddRecordModal(true)}
                  className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                >
                  Add First Record
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {records.map((record) => {
                  const Icon = getRecordIcon(record.record_type);
                  return (
                    <div key={record.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex items-start space-x-4">
                          <div className="bg-gray-100 p-2 rounded-lg">
                            <Icon className="w-6 h-6 text-gray-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{record.title}</h3>
                            <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
                              <span className="capitalize bg-gray-100 px-2 py-0.5 rounded text-xs">
                                {record.record_type.replace('_', ' ')}
                              </span>
                              <span>•</span>
                              <span>{formatDateForDisplay(record.date)}</span>
                            </div>
                            {record.description && (
                              <p className="text-sm text-gray-600 mt-2">{record.description}</p>
                            )}
                            {record.amount && (
                              <p className="text-sm font-medium text-gray-900 mt-2">
                                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(record.amount)}
                              </p>
                            )}
                            {record.url && (
                              <a 
                                href={record.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center text-sm text-blue-600 hover:text-blue-800 mt-2"
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                View Source
                              </a>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteRecord(record.id)}
                          className="text-gray-400 hover:text-red-600 p-1"
                          title="Delete Record"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'expenses' && (() => {
        const visibleExpenses = filterBillableOnly
          ? homeExpenses.filter((e) => e.billable_to_homeowner)
          : homeExpenses;
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-gray-500" />
                  Home Expenses
                </h2>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={filterBillableOnly}
                      onChange={(e) => setFilterBillableOnly(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-gray-300 accent-black"
                    />
                    <span className="text-xs text-gray-600">Billed to homeowner only</span>
                  </label>
                  <button
                    onClick={() => handleOpenExpenseModal()}
                    className="flex items-center space-x-1 px-3 py-1 text-sm bg-black text-white hover:bg-gray-800 rounded-md transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Expense</span>
                  </button>
                </div>
              </div>

              {!expensesLoading && visibleExpenses.length > 0 && (() => {
                const effectiveCost = (e: HomeExpense): number | null => {
                  if (e.internal_cost !== null && e.internal_cost !== undefined) return Number(e.internal_cost);
                  if (e.children && e.children.length > 0) {
                    const childSum = e.children.reduce((s, c) => s + Number(c.internal_cost || 0), 0);
                    return childSum > 0 ? childSum : null;
                  }
                  return null;
                };
                const totalExpenses = visibleExpenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
                const totalInternalCosts = visibleExpenses.reduce((sum, e) => sum + (effectiveCost(e) ?? 0), 0);
                const totalMargin = totalExpenses - totalInternalCosts;
                const fmt = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
                const marginPct = totalExpenses > 0 ? (totalMargin / totalExpenses) * 100 : 0;
                const withCostData = visibleExpenses.filter((e) => effectiveCost(e) !== null).length;
                return (
                  <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Summary{filterBillableOnly ? ' — Billed to Homeowner' : ''}
                      </span>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={showInternalCosts}
                          onChange={(e) => setShowInternalCosts(e.target.checked)}
                          className="h-3.5 w-3.5 rounded border-gray-300 accent-black"
                        />
                        <span className="text-xs text-gray-500">Show internal costs &amp; margin</span>
                      </label>
                    </div>
                    <div className={`grid gap-4 ${showInternalCosts ? 'grid-cols-3' : 'grid-cols-1'}`}>
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">Total Expenses</div>
                        <div className="text-xl font-semibold text-gray-900">{fmt(totalExpenses)}</div>
                        <div className="text-xs text-gray-400">{visibleExpenses.length} expense{visibleExpenses.length !== 1 ? 's' : ''}</div>
                      </div>
                      {showInternalCosts && (
                        <>
                          <div>
                            <div className="text-xs text-gray-500 mb-0.5">Internal Costs</div>
                            <div className="text-xl font-semibold text-gray-900">{fmt(totalInternalCosts)}</div>
                            <div className="text-xs text-gray-400">{withCostData} with cost data</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-0.5">Margin</div>
                            <div className={`text-xl font-semibold ${totalMargin >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                              {fmt(totalMargin)}
                            </div>
                            <div className="text-xs text-gray-400">{marginPct.toFixed(1)}% of revenue</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}

              {expensesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : homeExpenses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No home expenses yet.</div>
              ) : visibleExpenses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No expenses billed to the homeowner.</div>
              ) : (
                <div className="space-y-3">
                  {visibleExpenses.map((expense) => {
                    const cost = expense.internal_cost !== null && expense.internal_cost !== undefined
                      ? Number(expense.internal_cost)
                      : (expense.children && expense.children.length > 0)
                        ? (() => { const s = expense.children.reduce((a, c) => a + Number(c.internal_cost || 0), 0); return s > 0 ? s : null; })()
                        : null;
                    const fmt = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
                    return (
                    <div key={expense.id} className="rounded-lg border border-gray-200 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            {fmt(Number(expense.amount || 0))}
                            {expense.billable_to_homeowner && (
                              <span className="text-xs font-normal px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">Billed</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-700 mt-1">
                            {expense.description || getExpenseContextLabel(expense)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {getExpenseContextLabel(expense)}
                            {expense.expense_date ? ` • ${formatDateForDisplay(expense.expense_date)}` : ''}
                            {expense.vendor ? ` • ${expense.vendor}` : ''}
                          </div>
                          {showInternalCosts && (
                            <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                              <span>
                                Cost:{' '}
                                <span className="font-medium text-gray-700">
                                  {cost !== null ? fmt(cost) : '—'}
                                </span>
                              </span>
                              {cost !== null && (
                                <span>
                                  Margin:{' '}
                                  <span className={`font-medium ${Number(expense.amount) - cost >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                                    {fmt(Number(expense.amount) - cost)}
                                  </span>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleOpenExpenseModal(expense)}
                            className="text-xs text-blue-700 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteHomeExpense(expense.id)}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ); })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {activeTab === 'systems' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold flex items-center">
                <Settings className="w-5 h-5 mr-2 text-gray-500" />
                Home Systems
              </h2>
              <button
                onClick={() => setShowConfigureSystemsModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm"
              >
                <Settings className="w-4 h-4" />
                <span>Configure Home Systems</span>
              </button>
            </div>

            {homeSystemsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : !homeSystems || ![
                'heating_systems', 'cooling_systems', 'appliances',
                'other_systems', 'upgrades', 'outdoor_features',
              ].some((k) => Array.isArray(homeSystems[k]) && homeSystems[k].length > 0) ? (
              <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center">
                <Settings className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-700">No home systems configured yet</p>
                <p className="mt-1 text-sm text-gray-500">
                  Configure this home's systems to generate a personalized maintenance plan.
                </p>
                <button
                  onClick={() => setShowConfigureSystemsModal(true)}
                  className="mt-4 inline-flex items-center space-x-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Configure Home Systems</span>
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {([
                  { key: 'heating_systems', label: 'Heating Systems', options: [
                    { id: 'furnace', label: 'Furnace' },
                    { id: 'heat_pump', label: 'Heat Pump' },
                    { id: 'boiler', label: 'Boiler' },
                    { id: 'radiant', label: 'Radiant Heat' },
                    { id: 'baseboard', label: 'Electric Baseboard' },
                    { id: 'fireplace', label: 'Fireplace' },
                  ]},
                  { key: 'cooling_systems', label: 'Cooling Systems', options: [
                    { id: 'central_ac', label: 'Central AC' },
                    { id: 'mini_split', label: 'Mini Split' },
                    { id: 'window_units', label: 'Window Units' },
                    { id: 'swamp_cooler', label: 'Swamp Cooler' },
                  ]},
                  { key: 'appliances', label: 'Appliances', options: [
                    { id: 'washer', label: 'Washer' },
                    { id: 'dryer', label: 'Dryer' },
                    { id: 'dishwasher', label: 'Dishwasher' },
                    { id: 'refrigerator', label: 'Refrigerator' },
                    { id: 'oven', label: 'Oven/Range' },
                    { id: 'microwave', label: 'Built-in Microwave' },
                    { id: 'garbage_disposal', label: 'Garbage Disposal' },
                  ]},
                  { key: 'other_systems', label: 'Other Systems', options: [
                    { id: 'generator', label: 'Generator' },
                    { id: 'water_softener', label: 'Water Softener' },
                    { id: 'water_filter', label: 'Water Filtration System' },
                    { id: 'sump_pump', label: 'Sump Pump' },
                    { id: 'security_system', label: 'Security System' },
                  ]},
                  { key: 'upgrades', label: 'Upgrades', options: [
                    { id: 'solar_panels', label: 'Solar Panels' },
                    { id: 'ev_charger', label: 'EV Charger' },
                    { id: 'electronic_locks', label: 'Electronic Locks' },
                    { id: 'smart_thermostat', label: 'Smart Thermostat' },
                    { id: 'whole_house_fan', label: 'Whole House Fan' },
                  ]},
                  { key: 'outdoor_features', label: 'Outdoor Features', options: [
                    { id: 'gutters', label: 'Gutters' },
                    { id: 'pool', label: 'Pool' },
                    { id: 'hot_tub', label: 'Hot Tub' },
                    { id: 'sprinklers', label: 'Sprinkler System' },
                    { id: 'deck', label: 'Deck' },
                    { id: 'patio', label: 'Patio' },
                    { id: 'fence', label: 'Fence' },
                  ]},
                ] as { key: string; label: string; options: { id: string; label: string }[] }[]).map(({ key, label, options }) => {
                  const items: string[] = homeSystems[key] || [];
                  if (items.length === 0) return null;
                  const planEnabled: string[] = homeSystems.maintenance_plan_enabled?.[key] || [];
                  return (
                    <div key={key}>
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        {label}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {items.map((id) => {
                          const opt = options.find((o) => o.id === id);
                          const hasPlan = planEnabled.includes(id);
                          return (
                            <span
                              key={id}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                                hasPlan
                                  ? 'bg-green-50 text-green-800 border border-green-200'
                                  : 'bg-gray-100 text-gray-700 border border-gray-200'
                              }`}
                            >
                              {opt?.label ?? id}
                              {hasPlan && <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                <p className="text-xs text-gray-400 pt-2">
                  Items with <CheckCircle2 className="w-3 h-3 inline text-green-600" /> have maintenance plans enabled.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'maintenance' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold flex items-center">
                <Wrench className="w-5 h-5 mr-2 text-gray-500" />
                Maintenance Plan
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleOpenCustomTaskModal}
                  className="flex items-center space-x-1 px-3 py-1 text-sm bg-black text-white hover:bg-gray-800 rounded-md transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Task</span>
                </button>
                <button
                  onClick={fetchMaintenancePlans}
                  disabled={maintenancePlansLoading}
                  className="flex items-center space-x-2 px-3 py-1 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                >
                  {maintenancePlansLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <span>Refresh</span>
                  )}
                </button>
              </div>
            </div>

            {maintenancePlansLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : maintenancePlans.length === 0 ? (
              <div className="text-center py-8">
                <Wrench className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Maintenance Plans</h3>
                <p className="text-gray-600">No maintenance plans have been generated for this home.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {maintenancePlans.map((plan) => {
                  const task = plan.maintenance_tasks;
                  const template = plan.maintenance_templates;
                  const isOverdue = plan.next_due_date && new Date(plan.next_due_date) < new Date();
                  const daysUntilDue = plan.next_due_date 
                    ? Math.floor((new Date(plan.next_due_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                    : null;
                  
                  // Handle custom vs template tasks
                  const taskName = plan.is_custom ? plan.custom_task_name : task?.name || 'Unknown Task';
                  const taskDescription = plan.is_custom ? plan.custom_description : task?.description || '';
                  const taskFrequency = plan.is_custom ? plan.custom_frequency_months : task?.frequency_months;
                  
                  return (
                    <div key={plan.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">
                            {taskName}
                            {plan.is_custom && <span className="ml-2 text-xs text-purple-600 font-medium">(Custom)</span>}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">{taskDescription}</p>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {plan.status !== 'completed' && (
                            <button
                              onClick={() => {
                                const taskForComplete: Task = {
                                  id: plan.id,
                                  title: task?.name || '',
                                  description: task?.description || '',
                                  category_id: null,
                                  status: plan.status as Task['status'],
                                  priority: (template?.priority || 'normal') as Task['priority'],
                                  due_date: plan.next_due_date,
                                  completed_at: plan.last_completed_date,
                                  home_id: plan.home_id,
                                  category_name: 'Maintenance',
                                  skill_level: template?.professional_required ? 'professional' : 'diy',
                                  task_type: 'maintenance_task',
                                  frequency_months: task?.frequency_months || null,
                                  maintenance_task_id: plan.task_id,
                                  completed_by: plan.completed_by,
                                  completion_notes: plan.completion_notes,
                                };
                                handleOpenCompleteModal(taskForComplete);
                              }}
                              className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm flex items-center space-x-1"
                              title="Mark as completed"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              <span>Complete</span>
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${
                          plan.status === 'completed' ? 'bg-green-100 text-green-800' :
                          isOverdue ? 'bg-red-100 text-red-800' :
                          daysUntilDue !== null && daysUntilDue <= 30 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {plan.status === 'completed' ? 'Completed' :
                           isOverdue ? 'Overdue' :
                           daysUntilDue !== null && daysUntilDue <= 30 ? 'Due Soon' :
                           plan.status}
                        </span>
                        {template?.priority && (
                          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs ${getPriorityColor(template.priority as Task['priority'])}`}>
                            {template.priority}
                          </span>
                        )}
                        {template?.professional_required && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-red-100 text-red-800">
                            Professional Required
                          </span>
                        )}
                        {taskFrequency && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800">
                            Every {taskFrequency} month{taskFrequency !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                        {plan.next_due_date && (
                          <div className={`flex items-center space-x-1 ${
                            isOverdue ? 'text-red-600' : daysUntilDue !== null && daysUntilDue <= 30 ? 'text-yellow-600' : ''
                          }`}>
                            <Calendar className="w-4 h-4" />
                            <span>
                              {isOverdue 
                                ? `Overdue by ${Math.abs(daysUntilDue || 0)} days`
                                : daysUntilDue === 0
                                ? 'Due today'
                                : daysUntilDue !== null && daysUntilDue <= 30
                                ? `Due in ${daysUntilDue} days`
                                : `Due: ${formatDateForDisplay(plan.next_due_date)}`
                              }
                            </span>
                          </div>
                        )}
                        {plan.last_completed_date && (
                          <div className="flex items-center space-x-1 text-green-600">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>Last done: {formatDateForDisplay(plan.last_completed_date)}</span>
                          </div>
                        )}
                        {plan.completed_by && (
                          <div className="flex items-center space-x-1">
                            {plan.completed_by === 'homeowner' ? (
                              <User className="w-4 h-4" />
                            ) : (
                              <Briefcase className="w-4 h-4" />
                            )}
                            <span>{plan.completed_by === 'homeowner' ? 'Homeowner' : 'Professional'}</span>
                          </div>
                        )}
                      </div>

                      {plan.completion_notes && (
                        <div className="mt-2 text-sm text-gray-600 italic bg-gray-50 p-2 rounded">
                          Note: "{plan.completion_notes}"
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Schedule Visit Modal */}
      {showScheduleVisitModal && home && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Schedule Visit</h3>
              <button
                onClick={() => {
                  setShowScheduleVisitModal(false);
                  setVisitFormData({
                    visit_type: 'service',
                    scheduled_date: '',
                    estimated_duration_minutes: visitDurationDefaults.service,
                    assigned_to: 'internal',
                    internal_name: '',
                    visit_notes: '',
                    booked_by: scheduleVisitCurrentUserId || '',
                    booking_source: 'admin_manual',
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <h4 className="font-medium text-sm">{home.address}</h4>
              <p className="text-sm text-gray-600 mt-1">{home.city}, {home.state} {home.zip_code}</p>
              <div className="text-xs text-gray-500 mt-2">
                Owner: {owner?.full_name}
              </div>
            </div>

            <form onSubmit={handleScheduleVisit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Internal Name <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={visitFormData.internal_name}
                  maxLength={120}
                  onChange={(e) => setVisitFormData({ ...visitFormData, internal_name: e.target.value })}
                  placeholder="e.g. Smith — Furnace tune-up"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Helps distinguish similar visits in admin lists. Not shown to homeowners or contractors.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visit Type
                </label>
                <select
                  value={visitFormData.visit_type}
                  onChange={(e) => {
                    const visitType = e.target.value as 'maintenance' | 'service' | 'walkthrough';
                    setVisitFormData({
                      ...visitFormData,
                      visit_type: visitType,
                      estimated_duration_minutes: visitDurationDefaults[visitType],
                      booked_by:
                        visitType === 'walkthrough'
                          ? visitFormData.booked_by || scheduleVisitCurrentUserId || ''
                          : visitFormData.booked_by,
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                >
                  <option value="service">Service Visit</option>
                  <option value="maintenance">Maintenance Visit</option>
                  <option value="walkthrough">Walkthrough</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scheduled Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={visitFormData.scheduled_date}
                  onChange={(e) => setVisitFormData({ ...visitFormData, scheduled_date: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration
                </label>
                <select
                  value={visitFormData.estimated_duration_minutes}
                  onChange={(e) =>
                    setVisitFormData({
                      ...visitFormData,
                      estimated_duration_minutes: parseInt(e.target.value, 10),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                >
                  {VISIT_DURATION_OPTIONS_MINUTES.map((minutes) => (
                    <option key={minutes} value={minutes}>
                      {formatVisitDurationLabel(minutes)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned To
                </label>
                <input
                  type="text"
                  value={visitFormData.assigned_to}
                  onChange={(e) => setVisitFormData({ ...visitFormData, assigned_to: e.target.value })}
                  placeholder="internal, contractor name, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              {visitFormData.visit_type === 'walkthrough' && (
                <div className="space-y-3 p-3 bg-slate-50 border border-slate-200 rounded-md">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                    Booking attribution
                  </p>
                  <p className="text-xs text-gray-500 -mt-1">
                    Who scheduled this walkthrough and how it was booked (used for performance reporting).
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="home-schedule-booked-by">
                        Booked by
                      </label>
                      <select
                        id="home-schedule-booked-by"
                        value={visitFormData.booked_by}
                        onChange={(e) =>
                          setVisitFormData({ ...visitFormData, booked_by: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm bg-white"
                      >
                        <option value="">Not set</option>
                        {scheduleVisitAdmins.map((admin) => (
                          <option key={admin.id} value={admin.id}>
                            {admin.full_name || 'Unnamed'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        className="block text-sm font-medium text-gray-700 mb-1"
                        htmlFor="home-schedule-booking-source"
                      >
                        Booking source
                      </label>
                      <select
                        id="home-schedule-booking-source"
                        value={visitFormData.booking_source}
                        onChange={(e) =>
                          setVisitFormData({
                            ...visitFormData,
                            booking_source: e.target.value as
                              | 'admin_manual'
                              | 'homeowner_request'
                              | 'automation',
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm bg-white"
                      >
                        {BOOKING_SOURCES.map((source) => (
                          <option key={source} value={source}>
                            {BOOKING_SOURCE_LABELS[source]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visit Notes (Optional)
                </label>
                <textarea
                  value={visitFormData.visit_notes}
                  onChange={(e) => setVisitFormData({ ...visitFormData, visit_notes: e.target.value })}
                  rows={3}
                  placeholder="Special instructions or notes..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowScheduleVisitModal(false);
                    setVisitFormData({
                      visit_type: 'service',
                      scheduled_date: '',
                      estimated_duration_minutes: visitDurationDefaults.service,
                      assigned_to: 'internal',
                      internal_name: '',
                      visit_notes: '',
                      booked_by: scheduleVisitCurrentUserId || '',
                      booking_source: 'admin_manual',
                    });
                  }}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={schedulingVisit}
                  className="flex-1 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {schedulingVisit ? 'Scheduling...' : 'Schedule Visit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showExpenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{editingExpense ? 'Edit Expense' : 'Add Home Expense'}</h3>
              <button
                onClick={() => {
                  setShowExpenseModal(false);
                  resetExpenseForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={expenseFormData.amount}
                  onChange={(e) => setExpenseFormData((prev) => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  value={expenseFormData.description}
                  onChange={(e) => setExpenseFormData((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={expenseFormData.expense_date}
                    onChange={(e) => setExpenseFormData((prev) => ({ ...prev, expense_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link to Visit</label>
                  <select
                    value={expenseFormData.visit_id}
                    onChange={(e) => setExpenseFormData((prev) => ({ ...prev, visit_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                  >
                    <option value="">No visit</option>
                    {visits.map((visitOption) => (
                      <option key={visitOption.id} value={visitOption.id}>
                        {visitOption.internal_name || `${visitOption.visit_type} visit`} - {formatDateForDisplay(visitOption.scheduled_date)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    value={expenseFormData.category}
                    onChange={(e) => setExpenseFormData((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                  <input
                    value={expenseFormData.vendor}
                    onChange={(e) => setExpenseFormData((prev) => ({ ...prev, vendor: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowExpenseModal(false);
                  resetExpenseForm();
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSaveHomeExpense()}
                disabled={savingExpense}
                className="flex-1 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                {savingExpense ? 'Saving...' : editingExpense ? 'Save Expense' : 'Create Expense'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Record Modal */}
      {showAddRecordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add Property Record</h3>
              <button
                onClick={() => setShowAddRecordModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddRecord} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Record Type
                </label>
                <select
                  value={recordFormData.record_type}
                  onChange={(e) => setRecordFormData({ ...recordFormData, record_type: e.target.value as PropertyRecord['record_type'] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                >
                  <option value="permit">Permit</option>
                  <option value="tax_assessment">Tax Assessment</option>
                  <option value="sale">Sale</option>
                  <option value="lien">Lien</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={recordFormData.title}
                  onChange={(e) => setRecordFormData({ ...recordFormData, title: e.target.value })}
                  required
                  placeholder="e.g., Roof Permit, 2024 Tax Assessment"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={recordFormData.date}
                  onChange={(e) => setRecordFormData({ ...recordFormData, date: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (Optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={recordFormData.amount}
                  onChange={(e) => setRecordFormData({ ...recordFormData, amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source URL (Optional)
                </label>
                <input
                  type="url"
                  value={recordFormData.url}
                  onChange={(e) => setRecordFormData({ ...recordFormData, url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={recordFormData.description}
                  onChange={(e) => setRecordFormData({ ...recordFormData, description: e.target.value })}
                  rows={3}
                  placeholder="Additional details..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddRecordModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingRecord}
                  className="flex-1 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {addingRecord ? 'Adding...' : 'Add Record'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Task Bottom Sheet */}
      {showCustomTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div 
            className="fixed inset-0" 
            onClick={handleCloseCustomTaskModal}
          />
          <div className="relative bg-white rounded-t-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl animate-slide-up">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
            </div>
            
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold">Add Maintenance Task</h3>
              <button
                onClick={handleCloseCustomTaskModal}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="overflow-y-auto px-6 py-4" style={{ maxHeight: 'calc(85vh - 180px)' }}>
              <div className="space-y-5">
                {/* Task Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Task Name *
                  </label>
                <input
                  type="text"
                  value={customTaskData.task_name}
                  onChange={(e) => setCustomTaskData({ ...customTaskData, task_name: e.target.value })}
                  placeholder="e.g., Clean koi pond filter"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={customTaskData.description}
                  onChange={(e) => setCustomTaskData({ ...customTaskData, description: e.target.value })}
                  placeholder="Add details about this maintenance task..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black resize-none"
                />
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  How often? *
                </label>
                <div className="inline-flex items-center space-x-2 bg-gray-50 px-3 py-2.5 rounded-md border border-gray-200">
                  <span className="text-[15px] text-gray-600 whitespace-nowrap">Every</span>
                  <input
                    type="number"
                    value={customTaskData.frequency_months}
                    onChange={(e) => setCustomTaskData({ ...customTaskData, frequency_months: e.target.value.replace(/[^0-9]/g, '') })}
                    min="1"
                    className="w-12 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-center text-lg font-bold bg-white"
                    style={{ display: 'inline-block' }}
                  />
                  <span className="text-[15px] text-gray-600 whitespace-nowrap">months</span>
                </div>
              </div>

              {/* First Due Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  When is it due?
                </label>
                <input
                  type="date"
                  value={customTaskData.first_due_date}
                  onChange={(e) => setCustomTaskData({ ...customTaskData, first_due_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              {/* Link to Inventory Item */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Inventory Item (Optional)
                </label>
                {inventoryItems.length > 0 ? (
                  <select
                    value={customTaskData.inventory_item_id}
                    onChange={(e) => setCustomTaskData({ ...customTaskData, inventory_item_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  >
                    <option value="">None</option>
                    {inventoryItems.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name} ({item.category})
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-gray-500">No inventory items found for this home</p>
                )}
              </div>

                {error && (
                  <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm flex items-center space-x-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span>{error}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Footer */}
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={handleCloseCustomTaskModal}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCustomTask}
                disabled={creatingCustomTask}
                className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {creatingCustomTask ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    <span>Add Task</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Completion Modal */}
      {showCompleteModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Mark Task Complete</h3>
              <button
                onClick={handleCloseCompleteModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-1">{selectedTask.title}</h4>
                <p className="text-sm text-gray-600">{selectedTask.description}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  When was this completed?
                </label>
                <input
                  type="date"
                  value={completionData.completion_date}
                  onChange={(e) => setCompletionData({ ...completionData, completion_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Who did this work?
                </label>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setCompletionData({ ...completionData, performed_by: 'homeowner' })}
                    className={`w-full flex items-center space-x-2 px-4 py-3 border rounded-md transition-colors ${
                      completionData.performed_by === 'homeowner'
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <User className="w-5 h-5" />
                    <span>Homeowner did it themselves</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCompletionData({ ...completionData, performed_by: 'professional' })}
                    className={`w-full flex items-center space-x-2 px-4 py-3 border rounded-md transition-colors ${
                      completionData.performed_by === 'professional'
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Briefcase className="w-5 h-5" />
                    <span>Professional service</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={completionData.notes}
                  onChange={(e) => setCompletionData({ ...completionData, notes: e.target.value })}
                  rows={3}
                  placeholder="Add any notes about this maintenance..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black resize-none"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleCloseCompleteModal}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCompleteTask}
                  disabled={completing}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {completing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Mark Complete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {homeId && (
        <ConfigureHomeSystemsModal
          homeId={homeId}
          isOpen={showConfigureSystemsModal}
          onClose={() => setShowConfigureSystemsModal(false)}
          onSaved={() => {
            setShowConfigureSystemsModal(false);
            void fetchHomeSystems();
          }}
        />
      )}
    </div>
    </div>
    </AdminLayout>
  );
};

export default HomeDetail;
